<template>
  <b-modal
    :visible="value"
    title="Notification"
    size="lg"
    @hidden="onHidden"
    @hide="$emit('input', false)"
  >
    <!-- <b-overlay round="sm"></b-overlay> -->
    <div class="d-flex align-items-center mb-3">
      <b-avatar
        v-if="senderAvatar"
        :src="senderAvatar"
        variant="primary"
        class="mr-2"
      />
      <b-avatar
        v-else
        variant="primary"
        :text="
          notificationSender ? notificationSender.charAt(0).toUpperCase() : '?'
        "
        class="mr-2"
      />
      <strong>{{ notificationSender || 'Sender' }}</strong>
    </div>
    <div class="notif-body">
      <p class="notif-title mb-2">
        <span class="notif-label">Title:</span> {{ notificationTitle }}
      </p>
      <p class="notif-message mb-0">
        <span class="notif-label">Message: </span>
        <span v-html="notificationMessageHtml"></span>
      </p>
    </div>

    <div v-if="attachmentUrls.length" class="attachments-section mt-3">
      <span class="notif-label d-block mb-2">Attachments</span>
      <div class="attachments-list">
        <div
          v-for="(item, i) in attachmentUrls"
          :key="i"
          class="attachment-item"
        >
          <a
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
            class="attachment-link"
            :title="item.name"
          >
            <b-icon :icon="attachmentIcon(item.name)" class="attachment-icon" />
            <span class="attachment-name">{{ item.name }}</span>
          </a>
          <a
            href="#"
            class="attachment-download-btn"
            :title="'Download ' + item.name"
            @click.prevent="downloadAttachment(item, i)"
          >
            <b-icon icon="download" class="attachment-open-icon" />
          </a>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <b-button variant="success" class="mt-3" @click="$emit('input', false)">
        <b-icon icon="hand-thumbs-up" class="text-white" />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Api from '@/api'

export default {
  name: 'NotificationPopUpModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    notification: {
      type: Object,
      default: null,
    },
  },
  computed: {
    notificationTitle() {
      return (
        (this.notification &&
          (this.notification.title || this.notification.message)) ||
        'Notification'
      )
    },
    notificationMessage() {
      if (!this.notification) return ''
      if (this.notification.message) return this.notification.message

      const d = this.notification.data
      if (d && d.data) {
        if (d.data.message) return d.data.message
        if (d.data.description) return d.data.description
      }
      return ''
    },
    notificationSender() {
      if (!this.notification) return ''
      const d = this.notification
      if (d.sender_name) return d.sender_name
      return ''
    },
    senderAvatar() {
      if (!this.notification) return ''
      // same source used by NotificationsList.fileSrc for non-file notifications
      if (this.notification.actor_thumbnail)
        return this.notification.actor_thumbnail
      return ''
    },
    notificationMessageHtml() {
      const safe = this.escapeHtml(this.notificationMessage || '')
      return this.linkify(safe).replace(/\n/g, '<br>')
    },
    attachmentUrls() {
      if (!this.notification || !this.notification.data) return []
      const d = this.notification.data
      const raw =
        (d.data && d.data.data && d.data.data.attachment_urls) ||
        (d.data && d.data.attachment_urls) ||
        d.attachment_urls
      if (!Array.isArray(raw)) return []
      return raw
        .map((item) => {
          if (typeof item === 'string') {
            const name = this.getFileNameFromUrl(item)
            return { url: item, name: name || 'View attachment' }
          }
          const rawName =
            item.name || this.getFileNameFromUrl(item.url) || 'View attachment'
          const displayName = rawName.replace(/^[a-zA-Z0-9]{8}_/, '') || rawName
          return {
            url: item.url,
            name: displayName,
          }
        })
        .filter((item) => item.url)
    },
  },
  methods: {
    escapeHtml(str) {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    linkify(text) {
      return text.replace(/((https?:\/\/|www\.)[^\s<]+)/gi, (url) => {
        const href = url.startsWith('http') ? url : `https://${url}`
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`
      })
    },
    onHidden() {
      this.$emit('input', false)
      this.$emit('closed')
    },
    getFileNameFromUrl(url) {
      if (!url || typeof url !== 'string') return ''
      try {
        const pathname = new URL(url).pathname
        const segment = pathname.split('/').filter(Boolean).pop() || ''
        // Optional: remove leading "randomId_" (e.g. qimW7J6E_) for a cleaner name
        const withoutPrefix = segment.replace(/^[a-zA-Z0-9]{8}_/, '')
        return decodeURIComponent(withoutPrefix || segment)
      } catch (e) {
        return ''
      }
    },
    attachmentIcon(name) {
      if (!name) return 'file-earmark'
      const n = name.toLowerCase()
      if (n.endsWith('.pdf')) return 'file-earmark-pdf'
      if (n.match(/\.(jpe?g|png|gif|webp|svg)$/)) return 'file-earmark-image'
      return 'file-earmark'
    },

    downloadAttachment(item, index) {
      if (!this.notification || this.notification.id == null) return
      const url = `${Api.defaults.baseURL}/notifications/${this.notification.id}/attachment/${index}`
      Api.get(url, { responseType: 'blob' })
        .then((res) => {
          const blob = res.data
          const name = item.name || 'attachment'
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = name
          link.click()
          window.URL.revokeObjectURL(link.href)
        })
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.notif-body {
  color: #111827; // dark text
}

.notif-title,
.notif-message {
  color: #111 !important;
}

.notif-label {
  color: #000 !important;
  font-weight: 700;
}

.label {
  color: #000; // label even darker
  font-weight: 700;
}

.attachments-section {
  .attachments-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .attachment-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin-bottom: 0.5rem;

    .attachment-link {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #006cff;
      text-decoration: none;
      &:hover {
        color: #0052cc;
      }
    }
    .attachment-download-btn {
      color: #006cff;
      padding: 0.25rem;
      &:hover {
        color: #0052cc;
      }
    }
  }

  .attachment-icon {
    flex-shrink: 0;
    font-size: 1.25rem;
    color: #64748b;
  }

  .attachment-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .attachment-open-icon {
    flex-shrink: 0;
    font-size: 0.875rem;
    opacity: 0.7;
  }
}
</style>
